/**
 * Rule evaluation against a post-scenario financial aggregate (SE-7002).
 *
 * This is a THIN stand-in for the FC-0.1 rule-evaluation service, which does
 * not exist yet. It lives behind the adapter seam and models exactly the four
 * layers the Live Impact design speaks about — squad cost ratio, wage budget,
 * registration and PSR — and nothing more. When FC-0.1 lands, the adapter
 * calls it instead and this module goes away; the shape it produces
 * (`ComplianceRuleEvaluation[]`) is what the panel actually consumes, so the
 * swap does not reach any component.
 *
 * Deliberately NOT modelled here, because the data to do it honestly does not
 * exist in the client: the design's "Foreign slots 18 / 17" headline needs
 * per-player nationality against a registration regime (FC/SE-7001). The
 * registration layer therefore evaluates squad SIZE, which the scenario really
 * does move, and the nationality dimension is left to the backend.
 * @module features/financial-compliance/domain/evaluateRules
 */

import type { ComplianceRuleEvaluation, RuleState } from "../types";
import {
  formatMillionsTight,
  formatPercent,
  formatPercentCap,
} from "./formatImpact";

/** The club's financial position before any scenario action is applied. */
export interface ClubFinancialBaseline {
  /** Current annual wage bill. */
  readonly wagePerYear: number;
  /** Current annual amortisation charge. */
  readonly amortisationPerYear: number;
  /** Current carrying value of registrations. */
  readonly netBookValue: number;
  /** Registered squad size today. */
  readonly squadSize: number;
  /** Annual revenue — the denominator of the squad cost ratio. */
  readonly revenuePerYear: number;
}

/** A configured squad-cost-ratio rule. */
export interface SquadCostRatioRule {
  /** Ratio at which the rule turns RED, where 0.7 means 70%. */
  readonly capRatio: number;
  /** Ratio at which the rule turns AMBER. */
  readonly warnRatio: number;
}

/** A configured wage-budget rule. */
export interface WageBudgetRule {
  /** Annual wage budget in currency units. */
  readonly budgetPerYear: number;
  /** Fraction of the budget at which the rule turns AMBER. */
  readonly warnFraction: number;
}

/** A configured squad-registration rule. */
export interface RegistrationRule {
  /** Maximum registered squad size. */
  readonly maxSquadSize: number;
}

/**
 * The club's configured compliance regime.
 *
 * Every rule is OPTIONAL and an absent rule is rendered as unconfigured — the
 * day-one state for every club is that none of these are set.
 */
export interface ComplianceRuleConfig {
  /** Squad cost ratio rule, when configured. */
  readonly squadCostRatio?: SquadCostRatioRule;
  /** Wage budget rule, when configured. */
  readonly wageBudget?: WageBudgetRule;
  /** Squad registration rule, when configured. */
  readonly registration?: RegistrationRule;
}

/** The aggregate after every staged action has been applied. */
export interface PostScenarioAggregate {
  /** Annual wage bill after the scenario. */
  readonly wagePerYear: number;
  /** Annual amortisation charge after the scenario. */
  readonly amortisationPerYear: number;
  /** Carrying value after the scenario. */
  readonly netBookValue: number;
  /** Registered squad size after the scenario. */
  readonly squadSize: number;
  /** Annual revenue (unchanged by squad actions). */
  readonly revenuePerYear: number;
}

/** Layer identities, exported so the panel and its tests agree on them. */
export const LAYER_IDS = {
  squadCostRatio: "squad-cost-ratio",
  wageBudget: "wage-budget",
  registration: "registration",
  psr: "psr",
} as const;

/** Human labels for each layer. */
const LAYER_LABELS: Record<string, string> = {
  [LAYER_IDS.squadCostRatio]: "Squad cost ratio",
  [LAYER_IDS.wageBudget]: "Wage budget",
  [LAYER_IDS.registration]: "Registration",
  [LAYER_IDS.psr]: "PSR",
};

/**
 * Build an unconfigured evaluation for a layer that has no rule set up.
 * @param layerId - The layer with no configured rule.
 * @returns The unconfigured evaluation for that layer.
 */
const unconfigured = (layerId: string): ComplianceRuleEvaluation => ({
  ruleId: `${layerId}:unconfigured`,
  layerId,
  layerLabel: LAYER_LABELS[layerId] ?? layerId,
  state: "not-configured",
  headline: `${LAYER_LABELS[layerId] ?? layerId} — not configured`,
});

/**
 * Squad cost ratio: (wages + amortisation) / revenue against the cap.
 * @param rule - The configured rule.
 * @param aggregate - The post-scenario aggregate.
 * @returns The evaluation for the squad-cost-ratio layer.
 */
const evaluateSquadCostRatio = (
  rule: SquadCostRatioRule,
  aggregate: PostScenarioAggregate
): ComplianceRuleEvaluation => {
  const ratio =
    aggregate.revenuePerYear > 0
      ? (aggregate.wagePerYear + aggregate.amortisationPerYear) /
        aggregate.revenuePerYear
      : 0;
  const state: RuleState =
    ratio > rule.capRatio
      ? "breach"
      : ratio > rule.warnRatio
        ? "at-risk"
        : "on-track";
  const relation = state === "breach" ? "over" : "within";
  return {
    ruleId: "squad-cost-ratio",
    layerId: LAYER_IDS.squadCostRatio,
    layerLabel: LAYER_LABELS[LAYER_IDS.squadCostRatio] ?? "",
    state,
    headline: `SCR ${formatPercent(ratio)} — ${relation} the ${formatPercentCap(rule.capRatio)} cap`,
  };
};

/**
 * Wage budget: the post-scenario wage bill against the configured budget.
 * @param rule - The configured rule.
 * @param aggregate - The post-scenario aggregate.
 * @returns The evaluation for the wage-budget layer.
 */
const evaluateWageBudget = (
  rule: WageBudgetRule,
  aggregate: PostScenarioAggregate
): ComplianceRuleEvaluation => {
  const headroom = rule.budgetPerYear - aggregate.wagePerYear;
  const state: RuleState =
    headroom < 0
      ? "breach"
      : aggregate.wagePerYear > rule.budgetPerYear * rule.warnFraction
        ? "at-risk"
        : "on-track";
  const relation = headroom < 0 ? "over" : "under";
  return {
    ruleId: "wage-budget",
    layerId: LAYER_IDS.wageBudget,
    layerLabel: LAYER_LABELS[LAYER_IDS.wageBudget] ?? "",
    state,
    headline: `${formatMillionsTight(headroom)} ${relation} the wage budget`,
  };
};

/**
 * Registration: post-scenario squad size against the registration limit.
 * @param rule - The configured rule.
 * @param aggregate - The post-scenario aggregate.
 * @returns The evaluation for the registration layer.
 */
const evaluateRegistration = (
  rule: RegistrationRule,
  aggregate: PostScenarioAggregate
): ComplianceRuleEvaluation => {
  const state: RuleState =
    aggregate.squadSize > rule.maxSquadSize
      ? "breach"
      : aggregate.squadSize === rule.maxSquadSize
        ? "at-risk"
        : "on-track";
  const relation = state === "breach" ? "over limit" : "within limit";
  return {
    ruleId: "registration",
    layerId: LAYER_IDS.registration,
    layerLabel: LAYER_LABELS[LAYER_IDS.registration] ?? "",
    state,
    headline: `Squad ${aggregate.squadSize} / ${rule.maxSquadSize} — ${relation}`,
  };
};

/**
 * Evaluate every layer against the post-scenario aggregate.
 *
 * Every layer is always present in the output — an unconfigured one as an
 * unconfigured evaluation — so the panel can render "not configured" rather
 * than dropping the row and implying compliance.
 * @param config - The club's configured regime.
 * @param aggregate - The post-scenario financial aggregate.
 * @returns One evaluation per layer, in render order.
 */
export const evaluateRules = (
  config: ComplianceRuleConfig,
  aggregate: PostScenarioAggregate
): readonly ComplianceRuleEvaluation[] => [
  config.squadCostRatio
    ? evaluateSquadCostRatio(config.squadCostRatio, aggregate)
    : unconfigured(LAYER_IDS.squadCostRatio),
  config.wageBudget
    ? evaluateWageBudget(config.wageBudget, aggregate)
    : unconfigured(LAYER_IDS.wageBudget),
  config.registration
    ? evaluateRegistration(config.registration, aggregate)
    : unconfigured(LAYER_IDS.registration),
  // PSR has no client-side model at all: its thresholds are a regime FC-0.1
  // owns, and inventing them would be a guess. It renders as the unconfigured
  // layer the design already shows.
  unconfigured(LAYER_IDS.psr),
];
